import type { QuestionData } from 'src/types/formQuestionnaire';
import type { LanguageOption } from 'src/utils/languages';
import { markQuestionErrors } from '../components/Header/helpers/formSubmitHelpers';
import {
  DEFAULT_FORM_EDITING_LOCALE,
  denormalizeQuestionForEditingLocale,
} from './localizedQuestionFieldHelpers';
import { isQuestionCountedForPublish } from './questionPublishScope';
import {
  validateQuestionDescription,
  validateQuestionText,
} from './validation';

export type FormPublishTranslationIssue =
  | {
      kind: 'questionText';
      localeValue: string;
      localeLabel: string;
      questionId: string;
      message: string;
    }
  | {
      kind: 'questionDescription';
      localeValue: string;
      localeLabel: string;
      questionId: string;
      message: string;
    }
  | {
      kind: 'optionsBlock';
      localeValue: string;
      localeLabel: string;
      questionId: string;
      message: string;
    }
  | {
      kind: 'optionText';
      localeValue: string;
      localeLabel: string;
      questionId: string;
      optionId: string;
      message: string;
    };

export function clearFormPublishTranslationUiErrors(
  questions: QuestionData[],
): { next: QuestionData[]; changed: boolean } {
  let changed = false;
  const next = questions.map((q) => {
    let qChanged = false;
    const translations = q.translations?.map(
      ({
        uiPublishQuestionTextError,
        uiPublishDescriptionError,
        uiPublishOptionsError,
        ...t
      }) => {
        if (
          uiPublishQuestionTextError != null ||
          uiPublishDescriptionError != null ||
          uiPublishOptionsError != null
        ) {
          qChanged = true;
        }
        return t;
      },
    );
    const options = q.options?.map((opt) => {
      const tr = opt.translations?.map(
        ({ uiPublishOptionTextError, ...ot }) => {
          if (uiPublishOptionTextError != null) qChanged = true;
          return ot;
        },
      );
      return { ...opt, translations: tr };
    });
    if (qChanged) changed = true;
    return { ...q, translations, options };
  });
  return { next, changed };
}

export function collectFormPublishTranslationIssues(
  questions: QuestionData[],
  selectedLanguages: LanguageOption[],
  isCoI?: boolean,
): FormPublishTranslationIssue[] {
  const locales = selectedLanguages.filter(
    (l) => l.value !== DEFAULT_FORM_EDITING_LOCALE,
  );
  if (!locales.length) return [];

  const out: FormPublishTranslationIssue[] = [];

  for (const lang of locales) {
    for (const q of questions) {
      if (!isQuestionCountedForPublish(q)) continue;
      const dq = denormalizeQuestionForEditingLocale(q, lang.value);
      const [marked] = markQuestionErrors([dq], isCoI);
      const qt = validateQuestionText(marked.questionText, isCoI);
      if (qt) {
        out.push({
          kind: 'questionText',
          localeValue: lang.value,
          localeLabel: lang.label,
          questionId: q.id,
          message: qt,
        });
      }
      const qd = validateQuestionDescription(marked.description);
      if (qd) {
        out.push({
          kind: 'questionDescription',
          localeValue: lang.value,
          localeLabel: lang.label,
          questionId: q.id,
          message: qd,
        });
      }
      if (marked.optionsError) {
        out.push({
          kind: 'optionsBlock',
          localeValue: lang.value,
          localeLabel: lang.label,
          questionId: q.id,
          message: marked.optionsError,
        });
      }
      marked.options?.forEach((opt) => {
        if (!opt.isOtherOption && opt.textError) {
          out.push({
            kind: 'optionText',
            localeValue: lang.value,
            localeLabel: lang.label,
            questionId: q.id,
            optionId: opt.id,
            message: opt.textError,
          });
        }
      });
    }
  }

  return out;
}

function upsertQuestionTranslationPublishErrors(
  existing: QuestionData['translations'],
  languageCode: string,
  patch: {
    uiPublishQuestionTextError?: string | null;
    uiPublishDescriptionError?: string | null;
    uiPublishOptionsError?: string | null;
  },
): NonNullable<QuestionData['translations']> {
  const list = [...(existing ?? [])];
  const idx = list.findIndex((t) => t.languageCode === languageCode);
  const prev = idx >= 0 ? list[idx] : undefined;
  const base = prev ?? {
    languageCode,
    questionText: '',
    description: null as string | null,
  };
  const nextRow = { ...base, ...patch };
  if (idx >= 0) {
    list[idx] = nextRow;
  } else {
    list.push(nextRow);
  }
  return list;
}

function upsertOptionTranslationPublishError(
  existing: QuestionData['options'],
  optionId: string,
  languageCode: string,
  message: string,
): QuestionData['options'] {
  if (!existing) return existing;
  return existing.map((opt) => {
    if (opt.id !== optionId) return opt;
    const oList = [...(opt.translations ?? [])];
    const oIdx = oList.findIndex((t) => t.languageCode === languageCode);
    const prev = oIdx >= 0 ? oList[oIdx] : undefined;
    const row = {
      languageCode,
      optionText: prev?.optionText ?? '',
      uiPublishOptionTextError: message,
    };
    if (oIdx >= 0) {
      oList[oIdx] = row;
    } else {
      oList.push(row);
    }
    return { ...opt, translations: oList };
  });
}

export function applyFormPublishTranslationIssuesToQuestions(
  questions: QuestionData[],
  issues: FormPublishTranslationIssue[],
): QuestionData[] {
  if (!issues.length) return questions;

  const byQuestion = new Map<string, FormPublishTranslationIssue[]>();
  for (const issue of issues) {
    const list = byQuestion.get(issue.questionId) ?? [];
    list.push(issue);
    byQuestion.set(issue.questionId, list);
  }

  return questions.map((q) => {
    const qIssues = byQuestion.get(q.id);
    if (!qIssues?.length) {
      return q;
    }

    let next: QuestionData = {
      ...q,
      isExpanded: true,
      textTouched: true,
      descriptionTouched: true,
      answerTypeTouched: true,
    };

    const localeToQuestionPatch = new Map<
      string,
      {
        uiPublishQuestionTextError?: string | null;
        uiPublishDescriptionError?: string | null;
        uiPublishOptionsError?: string | null;
      }
    >();

    for (const issue of qIssues) {
      if (
        issue.kind === 'questionText' ||
        issue.kind === 'questionDescription' ||
        issue.kind === 'optionsBlock'
      ) {
        const cur = localeToQuestionPatch.get(issue.localeValue) ?? {};
        if (issue.kind === 'questionText') {
          cur.uiPublishQuestionTextError = issue.message;
        } else if (issue.kind === 'questionDescription') {
          cur.uiPublishDescriptionError = issue.message;
        } else {
          cur.uiPublishOptionsError = issue.message;
        }
        localeToQuestionPatch.set(issue.localeValue, cur);
      }
    }

    for (const [localeValue, patch] of localeToQuestionPatch) {
      next = {
        ...next,
        translations: upsertQuestionTranslationPublishErrors(
          next.translations,
          localeValue,
          patch,
        ),
      };
    }

    for (const issue of qIssues) {
      if (issue.kind === 'optionText') {
        next = {
          ...next,
          options: upsertOptionTranslationPublishError(
            next.options,
            issue.optionId,
            issue.localeValue,
            issue.message,
          ),
        };
      }
    }

    return next;
  });
}

export function uniqueFormPublishLocaleLabels(
  issues: FormPublishTranslationIssue[],
): string[] {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const issue of issues) {
    if (!seen.has(issue.localeValue)) {
      seen.add(issue.localeValue);
      labels.push(issue.localeLabel);
    }
  }
  return labels;
}
